import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { format, isToday, isYesterday } from "date-fns";
import {
  FaPaperclip, FaMicrophone, FaStop, FaPaperPlane,
  FaTimes, FaFilePdf, FaFileAlt, FaRegCommentDots,
} from "react-icons/fa";
import { ArrowLeft } from "lucide-react";
import "./messages.css";

/* ── Constantes ── */
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo
const IMAGE_RE = /\.(jpeg|jpg|png|gif|webp)$/i;
const AUDIO_RE = /\.(webm|mp3|wav|ogg)$/i;
const PDF_RE   = /\.pdf$/i;

/* ── Helpers ── */
const dayLabel = (dateStr) => {
  const d = new Date(dateStr);
  if (isToday(d))     return "Aujourd'hui";
  if (isYesterday(d)) return "Hier";
  return format(d, "dd MMM yyyy");
};

const groupByDay = (msgs) =>
  msgs.reduce((acc, msg) => {
    const key = format(new Date(msg.createdAt), "yyyy-MM-dd");
    if (!acc[key]) acc[key] = { label: dayLabel(msg.createdAt), messages: [] };
    acc[key].messages.push(msg);
    return acc;
  }, {});

const formatDuration = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} Ko`
    : `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;

const Messages = ({ onClose }) => {
  const [content,      setContent]      = useState("");
  const [file,         setFile]         = useState(null);
  const [audioBlob,    setAudioBlob]    = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isRecording,  setIsRecording]  = useState(false);
  const [recordSecs,   setRecordSecs]   = useState(0);
  const [isSending,    setIsSending]    = useState(false);
  const [error,        setError]        = useState("");
  const [preview,      setPreview]      = useState(null); // image en plein écran

  const mediaRecorderRef = useRef(null);
  const streamRef        = useRef(null);
  const timerRef         = useRef(null);
  const audioChunksRef   = useRef([]);
  const messagesEndRef   = useRef(null);
  const inputRef         = useRef(null);
  const fileInputRef     = useRef(null);

  const recipientEmail = "admin1@gmail.com";
  const userEmail      = localStorage.getItem("userEmail");
  const API_URL        = process.env.REACT_APP_API_URL;

  /* URL locale de l'aperçu audio : créée une seule fois, puis libérée */
  const audioUrl = useMemo(
    () => (audioBlob ? URL.createObjectURL(audioBlob) : null),
    [audioBlob]
  );
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  /* ── Scroll : uniquement quand un nouveau message arrive ── */
  const lastId = conversation[conversation.length - 1]?._id;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [lastId]);

  /* ── Focus initial sur le champ de saisie ── */
  useEffect(() => { inputRef.current?.focus(); }, []);

  /* ── Fermeture avec Échap (ferme d'abord l'aperçu, puis le popup) ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (preview) setPreview(null);
      else onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, preview]);

  /* ── Blocage du scroll de la page en arrière-plan ── */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* ── Erreur : disparaît seule ── */
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 6000);
    return () => clearTimeout(t);
  }, [error]);

  /* ── Zone de saisie : hauteur automatique ── */
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [content]);

  /* ── Nettoyage à la fermeture (micro, chrono) ── */
  useEffect(() => () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  /* ── Récupération de la conversation ── */
  const fetchConversation = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/messages/user/conversation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversation(data);
    } catch (err) {
      console.error("Fetch error:", err.response?.data || err.message);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchConversation();
    const interval = setInterval(fetchConversation, 15000);
    return () => clearInterval(interval);
  }, [fetchConversation]);

  /* ── Enregistrement vocal ── */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current   = [];

      mediaRecorderRef.current.ondataavailable = (e) =>
        audioChunksRef.current.push(e.data);

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setFile(null);
        stream.getTracks().forEach((t) => t.stop()); // libère le micro
      };

      mediaRecorderRef.current.start();
      setError("");
      setIsRecording(true);
      setRecordSecs(0);
      timerRef.current = setInterval(() => setRecordSecs((s) => s + 1), 1000);
    } catch {
      setError("Impossible d'accéder au microphone. Vérifiez les autorisations du navigateur.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  /* ── Fichier joint ── */
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      setError("Le fichier dépasse 10 Mo.");
      e.target.value = "";
      return;
    }
    setError("");
    setFile(f);
    setAudioBlob(null);
    inputRef.current?.focus();
  };

  const removeAttachment = () => {
    setFile(null);
    setAudioBlob(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ── Envoi ── */
  const canSend = !isRecording && !isSending && (content.trim() || file || audioBlob);

  const handleSend = async () => {
    const token = localStorage.getItem("token");
    if (!token || !canSend) return;

    const sentContent = content.trim();
    const sentFile    = file;
    const sentAudio   = audioBlob;

    const optimisticMsg = {
      _id:         `opt-${Date.now()}`,
      senderEmail: userEmail,
      content:     sentContent,
      filePath:    null,
      localLabel:  sentFile ? sentFile.name : sentAudio ? "Message vocal" : "",
      createdAt:   new Date().toISOString(),
      pending:     true,
    };
    setConversation((prev) => [...prev, optimisticMsg]);
    setContent(""); setFile(null); setAudioBlob(null); setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsSending(true);

    try {
      let response;
      if (!sentFile && !sentAudio) {
        response = await axios.post(
          `${API_URL}/messages`,
          { recipientEmail, senderEmail: userEmail, content: sentContent },
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
        );
      } else {
        const form = new FormData();
        form.append("recipientEmail", recipientEmail);
        form.append("senderEmail", userEmail);
        if (sentContent) form.append("content", sentContent);
        if (sentFile)    form.append("file", sentFile);
        if (sentAudio)   form.append("file", sentAudio, "voice-message.webm");
        response = await axios.post(`${API_URL}/messages`, form, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
      }
      setConversation((prev) =>
        prev.map((m) => (m._id === optimisticMsg._id ? response.data : m))
      );
    } catch (err) {
      setConversation((prev) => prev.filter((m) => m._id !== optimisticMsg._id));
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }
      /* on rend le message à l'utilisateur pour qu'il ne le perde pas */
      setContent(sentContent);
      setFile(sentFile);
      setAudioBlob(sentAudio);
      setError(`Message non envoyé : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const getFileUrl = (path) =>
    path ? `${API_URL.replace("/api", "")}/uploads/${path}` : null;

  const grouped = groupByDay(conversation);
  const isEmpty = conversation.length === 0;

  return createPortal(
    <div
      className="mx-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="mx-popup" role="dialog" aria-modal="true" aria-label="Messagerie support">
        {/* ── En-tête ── */}
        <div className="mx-header">
          <button className="mx-header__back" onClick={onClose} type="button" aria-label="Retour">
            <ArrowLeft size={12} />
          </button>

          <div className="mx-header__avatar" aria-hidden="true">
            A
            <span className="mx-header__dot" />
          </div>

          <div className="mx-header__info">
            <p className="mx-header__name">Support Admin</p>
            <p className="mx-header__status">En ligne</p>
          </div>
        </div>

        {/* ── Liste des messages ── */}
        <div className="mx-list" role="log" aria-live="polite">
          {isEmpty && (
            <div className="mx-empty">
              <div className="mx-empty__icon" aria-hidden="true">
                <FaRegCommentDots size={26} />
              </div>
              <p className="mx-empty__title">Aucun message pour le moment</p>
              <p className="mx-empty__text">
                Écrivez à l'équipe support, nous vous répondrons dès que possible.
              </p>
            </div>
          )}

          {Object.entries(grouped).map(([key, { label, messages }]) => (
            <React.Fragment key={key}>
              <p className="mx-date">{label}</p>

              {messages.map((msg) => {
                const url   = getFileUrl(msg.filePath);
                const isOwn = msg.senderEmail === userEmail;
                return (
                  <div
                    key={msg._id}
                    className={`mx-msg ${isOwn ? "mx-msg--own" : "mx-msg--other"}${msg.pending ? " mx-msg--pending" : ""}`}
                  >
                    {msg.content && <p className="mx-msg__text">{msg.content}</p>}

                    {msg.pending && msg.localLabel && (
                      <p className="mx-msg__file">
                        <FaFileAlt aria-hidden="true" /> <span>{msg.localLabel}</span>
                      </p>
                    )}

                    {msg.filePath && (
                      <>
                        {IMAGE_RE.test(msg.filePath) && (
                          <button
                            type="button"
                            className="mx-msg__img-btn"
                            onClick={() => setPreview(url)}
                            aria-label="Agrandir l'image"
                          >
                            <img src={url} alt="Pièce jointe" className="mx-msg__img" loading="lazy" />
                          </button>
                        )}
                        {AUDIO_RE.test(msg.filePath) && (
                          <audio controls preload="metadata" src={url} className="mx-msg__audio" />
                        )}
                        {PDF_RE.test(msg.filePath) && (
                          <a className="mx-msg__file" href={url} target="_blank" rel="noopener noreferrer">
                            <FaFilePdf aria-hidden="true" /> <span>Voir le PDF</span>
                          </a>
                        )}
                        {!IMAGE_RE.test(msg.filePath) &&
                          !AUDIO_RE.test(msg.filePath) &&
                          !PDF_RE.test(msg.filePath) && (
                            <a className="mx-msg__file" href={url} download>
                              <FaFileAlt aria-hidden="true" /> <span>Télécharger</span>
                            </a>
                          )}
                      </>
                    )}

                    <span className="mx-msg__time">
                      {msg.pending ? "Envoi…" : format(new Date(msg.createdAt), "H:mm")}
                    </span>
                  </div>
                );
              })}
            </React.Fragment>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Zone de saisie ── */}
        <div className="mx-composer">
          {error && (
            <div className="mx-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError("")} aria-label="Fermer le message d'erreur">
                <FaTimes size={11} />
              </button>
            </div>
          )}

          {isRecording && (
            <div className="mx-recording" role="status">
              <span className="mx-recording__dot" />
              Enregistrement en cours · {formatDuration(recordSecs)}
            </div>
          )}

          {(file || audioBlob) && (
            <div className="mx-attach">
              {audioBlob ? (
                <audio controls preload="metadata" src={audioUrl} className="mx-attach__audio" />
              ) : (
                <>
                  <FaFileAlt className="mx-attach__icon" aria-hidden="true" />
                  <span className="mx-attach__name">{file.name}</span>
                  <span className="mx-attach__size">{formatSize(file.size)}</span>
                </>
              )}
              <button
                type="button"
                className="mx-attach__remove"
                onClick={removeAttachment}
                aria-label="Retirer la pièce jointe"
              >
                <FaTimes size={12} />
              </button>
            </div>
          )}

          <div className="mx-row">
            <input
              ref={fileInputRef}
              type="file"
              id="file-input"
              accept="image/*,application/pdf,audio/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            <div className="mx-field">
              <button
                className="mx-icon-btn"
                type="button"
                aria-label="Joindre un fichier"
                onClick={() => fileInputRef.current?.click()}
                disabled={isRecording}
              >
                <FaPaperclip size={16} />
              </button>

              <textarea
                ref={inputRef}
                rows={1}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={file || audioBlob ? "Ajouter un message (facultatif)…" : "Écrivez votre message…"}
                disabled={isRecording}
                aria-label="Votre message"
                autoComplete="off"
              />

              <button
                className={`mx-icon-btn${isRecording ? " mx-icon-btn--recording" : ""}`}
                type="button"
                aria-label={isRecording ? "Arrêter l'enregistrement" : "Enregistrer un message vocal"}
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? <FaStop size={14} /> : <FaMicrophone size={16} />}
              </button>
            </div>

            <button
              className="mx-send"
              type="button"
              aria-label="Envoyer"
              onClick={handleSend}
              disabled={!canSend}
            >
              {isSending ? <span className="mx-spinner" /> : <FaPaperPlane size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Prévisualisation plein écran de l'image ── */}
      {preview && (
        <div
          className="mx-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu de l'image"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <button
            type="button"
            className="mx-lightbox__close"
            onClick={() => setPreview(null)}
            aria-label="Fermer l'aperçu"
          >
            <FaTimes size={18} />
          </button>

          <img src={preview} alt="Aperçu en plein écran" className="mx-lightbox__img" />

          <a
            className="mx-lightbox__open"
            href={preview}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ouvrir dans un nouvel onglet
          </a>
        </div>
      )}
    </div>,
    document.body
  );
};

export default Messages;